import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { ContentIdea, BrandVoice } from '@/types'
import { api, isApiError } from '@/api/client'
import { createRequestSequencer } from './utils/requestSequencer'

// Backend ContentCalendarEntry shape (snake_case from FastAPI).
// `publish_results` is optional on the wire — present only when the backend
// schema includes it (per-platform { success, error?, posted_at? } map).
interface CalendarEntry {
  id: string
  title: string | null
  body: string | null
  platforms: string[]
  status: string
  scheduled_at: string | null
  published_at: string | null
  publish_results?: Record<
    string,
    { success?: boolean; error?: string; status?: string; reason?: string }
  > | null
  media_urls?: string[]
  // Platform-keyed map of connected_account ids ({"instagram": "<uuid>"});
  // null / missing key = legacy resolve-by-platform.
  connected_account_ids?: Record<string, string> | null
}
interface CalendarResponse {
  entries: CalendarEntry[]
}

/**
 * Build a synthetic id for one (draft × platform) chip.
 * Format: "<draftId>__<platform>" — the calendar / store helpers below
 * round-trip through this so DnD and click handlers can recover the real
 * draft id and which platform the user clicked.
 */
export const composePlatformChipId = (draftId: string, platform: string) =>
  `${draftId}__${platform}`
export const decomposePlatformChipId = (
  id: string
): { draftId: string; platform: string | null } => {
  const i = id.indexOf('__')
  return i === -1
    ? { draftId: id, platform: null }
    : { draftId: id.slice(0, i), platform: id.slice(i + 2) }
}

/**
 * Extract `{ platform: errorMessage }` from a backend `publish_results` map.
 * Returns `undefined` when there are no per-platform errors so the field
 * stays absent on the resulting `ContentIdea` (no empty objects in state).
 */
function extractPublishErrors(
  publishResults: CalendarEntry['publish_results']
): Record<string, string> | undefined {
  if (!publishResults) return undefined
  const errors: Record<string, string> = {}
  for (const [platform, result] of Object.entries(publishResults)) {
    if (result && typeof result === 'object' && typeof result.error === 'string' && result.error) {
      errors[platform] = result.error
    }
  }
  return Object.keys(errors).length > 0 ? errors : undefined
}

/**
 * Platforms parked awaiting an OAuth reconnect (backend issue #2108).
 * The draft stays `scheduled` and publishes itself once the account is
 * reconnected, so without this the calendar shows a post whose time has
 * passed with nothing explaining why — the "silently blocked" state.
 */
function extractBlockedOnReconnect(
  publishResults: CalendarEntry['publish_results']
): string[] | undefined {
  if (!publishResults) return undefined
  const blocked = Object.entries(publishResults)
    .filter(
      ([, result]) =>
        result &&
        typeof result === 'object' &&
        result.status === 'skipped' &&
        result.reason === 'needs_reauth'
    )
    .map(([platform]) => platform)
  return blocked.length > 0 ? blocked : undefined
}

/**
 * Map a scheduled-draft calendar entry to one ContentIdea per platform so
 * a multi-platform draft renders as a separate chip per network.
 */
function mapCalendarEntryToIdeas(entry: CalendarEntry): ContentIdea[] {
  // Trust the draft's backend-validated platforms as-is. Unknown platforms
  // render via getPlatformBrand()'s Globe fallback (see DraggablePostChip)
  // rather than silently collapsing onto Twitter. An empty list still yields
  // a single placeholder chip so the post stays visible on the calendar.
  const platforms = entry.platforms.length > 0 ? entry.platforms : ['unknown']
  const publishErrors = extractPublishErrors(entry.publish_results)
  const blockedOnReconnect = extractBlockedOnReconnect(entry.publish_results)
  // Place the chip on scheduled_at, falling back to published_at so a
  // published-now post (scheduled_at may be null on legacy rows) still lands on
  // the calendar at its publish time — matches the backend COALESCE.
  const placementAt = entry.scheduled_at ?? entry.published_at ?? undefined
  const base = {
    title: entry.title ?? 'Scheduled post',
    content: entry.body ?? '',
    status: entry.status as ContentIdea['status'],
    scheduledAt: placementAt,
    publishedAt: entry.published_at ?? undefined,
    createdAt: placementAt ?? new Date().toISOString(),
    updatedAt: placementAt ?? new Date().toISOString(),
    mediaUrls: entry.media_urls ?? [],
    ...(publishErrors ? { publishErrors } : {}),
    ...(blockedOnReconnect ? { blockedOnReconnect } : {}),
  }
  return platforms.map((platform) => {
    const accountId = entry.connected_account_ids?.[platform]
    return {
      ...base,
      id: composePlatformChipId(entry.id, platform),
      platform,
      ...(accountId ? { accountId } : {}),
    }
  })
}

// Content store state interface
interface ContentState {
  // Calendar chips: one per draft and platform, from /content/calendar
  ideas: ContentIdea[]
  /** Chip ids and the draft ids behind them, so a callback that strips the
   *  platform suffix still resolves to its draft. */
  draftBackedIds: Set<string>
  selectedIdea: ContentIdea | null
  ideasLoading: boolean
  ideasError: string | null

  // Brand voice
  brandVoice: BrandVoice | null
  brandVoiceLoading: boolean
  brandVoiceError: string | null
}

// Content store actions interface
interface ContentActions {
  // Content ideas actions
  fetchIdeas: () => Promise<void>
  updateIdeaOptimistic: (id: string, updates: Partial<ContentIdea>) => Partial<ContentIdea> | null
  setSelectedIdea: (idea: ContentIdea | null) => void

  // Brand voice actions
  fetchBrandVoice: () => Promise<void>
  updateBrandVoice: (updates: Partial<BrandVoice>) => Promise<void>

  // Error clearing
  clearErrors: () => void
}

// Combined store type
type ContentStore = ContentState & ContentActions

// Initial state
const initialState: ContentState = {
  ideas: [],
  draftBackedIds: new Set<string>(),
  selectedIdea: null,
  ideasLoading: false,
  ideasError: null,

  brandVoice: null,
  brandVoiceLoading: false,
  brandVoiceError: null,
}

// Sequences overlapping fetchIdeas calls so a stale (out-of-order) resolution
// can never leave a stuck error banner over freshly loaded ideas (issue #1313).
const ideasFetchSeq = createRequestSequencer()

// Create the content store with devtools middleware
export const useContentStore = create<ContentStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // Content ideas actions
      fetchIdeas: async () => {
        const token = ideasFetchSeq.begin()
        set({ ideasLoading: true, ideasError: null })

        // Wide window so the calendar shows past + future scheduled drafts.
        const now = new Date()
        const start = new Date(now.getFullYear() - 1, 0, 1).toISOString()
        const end = new Date(now.getFullYear() + 1, 11, 31, 23, 59, 59).toISOString()

        try {
          const calendar = await api.get<CalendarResponse>('/content/calendar', {
            params: { start_date: start, end_date: end },
          })
          const entries = calendar.entries ?? []
          const draftBackedIds = new Set<string>()
          for (const entry of entries) {
            draftBackedIds.add(entry.id)
            for (const p of entry.platforms) {
              draftBackedIds.add(composePlatformChipId(entry.id, p))
            }
          }
          // A newer fetchIdeas superseded this one — discard the result so a
          // slow resolution can't clobber fresher state or resurface a stale error.
          if (!ideasFetchSeq.isCurrent(token)) return
          set({
            ideas: entries.flatMap(mapCalendarEntryToIdeas),
            draftBackedIds,
            ideasLoading: false,
          })
        } catch (error) {
          if (!ideasFetchSeq.isCurrent(token)) return
          set({
            ideasLoading: false,
            ideasError: isApiError(error) ? error.message : 'Failed to load the calendar',
          })
        }
      },

      updateIdeaOptimistic: (id: string, updates: Partial<ContentIdea>) => {
        const { ideas, draftBackedIds } = get()
        const idea = ideas.find((i) => i.id === id)
        if (!idea) return null

        // Capture previous values for the fields being updated
        const previousValues: Partial<ContentIdea> = {}
        for (const key of Object.keys(updates) as (keyof ContentIdea)[]) {
          ;(previousValues as Record<string, unknown>)[key] = idea[key]
        }

        if (!draftBackedIds.has(id)) return null
        const { draftId: targetDraftId } = decomposePlatformChipId(id)
        const matchesEntry = (i: ContentIdea) =>
          decomposePlatformChipId(i.id).draftId === targetDraftId

        // Optimistically update the store — all per-platform chips for the
        // same draft must move in lockstep.
        set((state) => ({
          ideas: state.ideas.map((i) => (matchesEntry(i) ? { ...i, ...updates } : i)),
          selectedIdea:
            state.selectedIdea?.id === id
              ? { ...state.selectedIdea, ...updates }
              : state.selectedIdea,
        }))

        const payload: Record<string, unknown> = {
          ...(updates.scheduledAt !== undefined && { scheduled_at: updates.scheduledAt }),
          ...(updates.status !== undefined && { status: updates.status }),
          ...(updates.title !== undefined && { title: updates.title }),
          ...(updates.content !== undefined && { body: updates.content }),
        }

        api.patch<ContentIdea>(`/content/drafts/${targetDraftId}`, payload).catch((error) => {
          set((state) => ({
            ideas: state.ideas.map((i) => (matchesEntry(i) ? { ...i, ...previousValues } : i)),
            selectedIdea:
              state.selectedIdea?.id === id
                ? { ...state.selectedIdea, ...previousValues }
                : state.selectedIdea,
            ideasError: isApiError(error) ? error.message : 'Failed to update content',
          }))
        })

        return previousValues
      },

      setSelectedIdea: (idea: ContentIdea | null) => {
        set({ selectedIdea: idea })
      },

      // Brand voice actions
      fetchBrandVoice: async () => {
        set({ brandVoiceLoading: true, brandVoiceError: null })
        try {
          const response = await api.get<BrandVoice[]>('/brand-voices')
          const voices = response ?? []
          const defaultVoice = voices.find((v) => v.is_default) ?? voices[0] ?? null
          set({ brandVoice: defaultVoice, brandVoiceLoading: false })
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to fetch brand voice'
          set({ brandVoiceError: message, brandVoiceLoading: false })
        }
      },

      updateBrandVoice: async (updates: Partial<BrandVoice>) => {
        set({ brandVoiceLoading: true, brandVoiceError: null })
        try {
          const existing = get().brandVoice
          // Fresh business with no voice yet → create one instead of PATCHing nothing
          const response = existing
            ? await api.patch<BrandVoice>(`/brand-voices/${existing.id}`, updates)
            : await api.post<BrandVoice>('/brand-voices', {
                name: 'Default Brand Voice',
                is_default: true,
                ...updates,
              })
          set({ brandVoice: response, brandVoiceLoading: false })
        } catch (error) {
          const message = isApiError(error) ? error.message : 'Failed to update brand voice'
          set({ brandVoiceError: message, brandVoiceLoading: false })
          throw error
        }
      },

      clearErrors: () => {
        set({
          ideasError: null,
          brandVoiceError: null,
        })
      },
    }),
    { name: 'ContentStore', enabled: import.meta.env.DEV }
  )
)

// Selectors for common access patterns
export const selectIdeas = (state: ContentStore) => state.ideas
export const selectSelectedIdea = (state: ContentStore) => state.selectedIdea
export const selectIdeasLoading = (state: ContentStore) => state.ideasLoading
export const selectIdeasError = (state: ContentStore) => state.ideasError

export const selectBrandVoice = (state: ContentStore) => state.brandVoice
export const selectBrandVoiceLoading = (state: ContentStore) => state.brandVoiceLoading
export const selectBrandVoiceError = (state: ContentStore) => state.brandVoiceError

// Filtered selectors
export const selectIdeasByStatus = (status: ContentIdea['status']) => (state: ContentStore) =>
  state.ideas.filter((idea) => idea.status === status)

export const selectIdeasByPlatform = (platform: ContentIdea['platform']) => (state: ContentStore) =>
  state.ideas.filter((idea) => idea.platform === platform)
